import { Auth } from 'aws-amplify';

import type { User } from '@/modules/Auth/userTypes';

/** CloudFormation pools often use `custom:tenant_id` / `custom:user_id` / `custom:user_role` in JWTs. */
function claim(payload: Record<string, unknown>, camel: string, snake: string): string {
  const a = payload[camel];
  const b = payload[snake];
  if (a !== undefined && a !== null && String(a) !== '') return String(a);
  if (b !== undefined && b !== null && String(b) !== '') return String(b);
  return '';
}

function firstAttr(attributes: Record<string, unknown> | undefined, keys: string[]): string {
  if (!attributes) return '';
  for (const k of keys) {
    const v = attributes[k];
    if (v !== undefined && v !== null && String(v) !== '') return String(v);
  }
  return '';
}

/** Build the Cognito-shaped `User` the rest of the app expects (tokens + attributes). */
export async function buildUserFromCognitoSession(): Promise<User | null> {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    const accessToken = session.getAccessToken();
    const refreshToken = session.getRefreshToken();
    const idPayload = idToken.decodePayload() as Record<string, unknown>;
    const accessPayload = accessToken.decodePayload() as Record<string, unknown>;

    const email = String(
      (cognitoUser.attributes?.email as string) || idPayload.email || idPayload['cognito:username'] || '',
    );

    return {
      username: cognitoUser.getUsername?.() || cognitoUser.username || email,
      pool: cognitoUser.pool as User['pool'],
      Session: null,
      client: cognitoUser.client as User['client'],
      signInUserSession: {
        idToken: {
          jwtToken: idToken.getJwtToken(),
          payload: {
            sub: String(idPayload.sub || ''),
            'cognito:groups': (idPayload['cognito:groups'] as string[]) || [],
            email_verified: Boolean(idPayload.email_verified),
            iss: String(idPayload.iss || ''),
            'cognito:username': String(idPayload['cognito:username'] || email),
            given_name: String(idPayload.given_name || cognitoUser.attributes?.given_name || ''),
            'custom:userId': claim(idPayload, 'custom:userId', 'custom:user_id'),
            origin_jti: String(idPayload.origin_jti || ''),
            'custom:tenantId': claim(idPayload, 'custom:tenantId', 'custom:tenant_id'),
            aud: String(idPayload.aud || ''),
            event_id: String(idPayload.event_id || ''),
            'custom:userRole': claim(idPayload, 'custom:userRole', 'custom:user_role'),
            token_use: String(idPayload.token_use || 'id'),
            auth_time: Number(idPayload.auth_time || 0),
            exp: Number(idPayload.exp || 0),
            iat: Number(idPayload.iat || 0),
            family_name: String(idPayload.family_name || cognitoUser.attributes?.family_name || ''),
            jti: String(idPayload.jti || ''),
            email,
          },
        },
        refreshToken: { token: refreshToken?.getToken?.() || '' },
        accessToken: {
          jwtToken: accessToken.getJwtToken(),
          payload: {
            sub: String(accessPayload.sub || ''),
            'cognito:groups': (accessPayload['cognito:groups'] as string[]) || [],
            iss: String(accessPayload.iss || ''),
            client_id: String(accessPayload.client_id || ''),
            origin_jti: String(accessPayload.origin_jti || ''),
            event_id: String(accessPayload.event_id || ''),
            token_use: String(accessPayload.token_use || 'access'),
            scope: String(accessPayload.scope || ''),
            auth_time: Number(accessPayload.auth_time || 0),
            exp: Number(accessPayload.exp || 0),
            iat: Number(accessPayload.iat || 0),
            jti: String(accessPayload.jti || ''),
            'custom:userRole': claim(accessPayload, 'custom:userRole', 'custom:user_role'),
            username: String(accessPayload.username || email),
          },
        },
        clockDrift: 0,
      },
      attributes: {
        email,
        email_verified: Boolean(cognitoUser.attributes?.email_verified ?? idPayload.email_verified),
        family_name: String(cognitoUser.attributes?.family_name || idPayload.family_name || ''),
        given_name: String(cognitoUser.attributes?.given_name || idPayload.given_name || ''),
        sub: String(cognitoUser.attributes?.sub || idPayload.sub || ''),
        custom: {
          tenantId: firstAttr(cognitoUser.attributes as Record<string, unknown>, [
            'custom:tenantId',
            'custom:tenant_id',
          ]) || claim(idPayload, 'custom:tenantId', 'custom:tenant_id'),
          userId: firstAttr(cognitoUser.attributes as Record<string, unknown>, [
            'custom:userId',
            'custom:user_id',
          ]) || claim(idPayload, 'custom:userId', 'custom:user_id'),
          userRole: firstAttr(cognitoUser.attributes as Record<string, unknown>, [
            'custom:userRole',
            'custom:user_role',
          ]) || claim(idPayload, 'custom:userRole', 'custom:user_role'),
        },
      },
    };
  } catch {
    return null;
  }
}
